/**
 * KRA Photo Journal – API Client
 *
 * Thin fetch wrapper that communicates with the Cloudflare Worker API.
 * All requests go through the Worker; the frontend never touches Airtable directly.
 *
 * Auth: JWT stored in localStorage is sent as Authorization: Bearer {token}
 * on photo-mutation requests (PATCH).
 */

import type { PhotosAPIResponse } from "@kra/photo-journal-shared";

const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";
const TOKEN_KEY = "kra_token";

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

class APIError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string
  ) {
    super(message);
    this.name = "APIError";
  }
}

async function apiFetch<T>(path: string, init?: RequestInit, withAuth = false): Promise<T> {
  const url = `${API_BASE}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> ?? {}),
  };

  if (withAuth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const resp = await fetch(url, { ...init, headers });

  if (!resp.ok) {
    let code: string | undefined;
    try {
      const body = (await resp.json()) as { error?: string; code?: string };
      code = body.code;
      throw new APIError(resp.status, body.error ?? "Request failed", code);
    } catch (e) {
      if (e instanceof APIError) throw e;
      throw new APIError(resp.status, "Request failed");
    }
  }

  return resp.json() as Promise<T>;
}

// ── Photos ────────────────────────────────────────────────────────────────────

export interface FetchPhotosParams {
  offset?: string;
  pageSize?: number;
  photographer?: string;
  sort?: "returnDate" | "shootDate";
  sortDir?: "desc" | "asc";
  search?: string;
  /** Fetch only these specific record IDs (for collection filtering) */
  recordIds?: string[];
  /** Drive category folder name — filters server-side on `Admin_category_folder` */
  folder?: string;
  /** Only sets in the admin's Drive that have no category folder yet (backfill) */
  missingFolder?: boolean;
  /** Quick-filter chip — filtered server-side (the feed is paginated) */
  quick?: "liked" | "downloaded" | "not-downloaded";
  /** With `quick: "liked"`, narrow to sets this photographer liked.
   *  Omit for the admin view = liked by anyone. */
  likedBy?: string;
}

export async function fetchPhotos(
  params: FetchPhotosParams = {}
): Promise<PhotosAPIResponse> {
  const query = new URLSearchParams();
  if (params.offset) query.set("offset", params.offset);
  if (params.pageSize) query.set("pageSize", String(params.pageSize));
  if (params.photographer) query.set("photographer", params.photographer);
  if (params.sort) query.set("sort", params.sort);
  if (params.sortDir) query.set("sortDir", params.sortDir);
  if (params.search) query.set("search", params.search);
  if (params.recordIds?.length) query.set("recordIds", params.recordIds.join(","));
  if (params.folder) query.set("folder", params.folder);
  if (params.missingFolder) query.set("missingFolder", "1");
  if (params.quick) query.set("quick", params.quick);
  if (params.likedBy) query.set("likedBy", params.likedBy);

  const qs = query.toString();
  return apiFetch<PhotosAPIResponse>(`/photos${qs ? `?${qs}` : ""}`);
}

export async function fetchPhotographers(): Promise<string[]> {
  const data = await apiFetch<{ photographers: string[] }>("/photos/photographers");
  return data.photographers;
}

export type TagOptions = Record<"kimono_tag" | "shoot_tag" | "season_tag" | "scene_tag" | "kimono_color_tag", string[]>;

export async function fetchTagOptions(): Promise<TagOptions> {
  return apiFetch<TagOptions>("/photos/tag-options");
}

// ── Drive category folders ────────────────────────────────────────────────────

export interface DriveFolderCount {
  name: string;
  count: number;
}

/** Distinct Drive category folders across the whole table (KV-cached 1h).
 *  `refresh` forces a rescan — used after the admin backfill writes folders. */
export async function fetchDriveFolders(refresh = false): Promise<DriveFolderCount[]> {
  const data = await apiFetch<{ folders: DriveFolderCount[] }>(
    `/photos/folders${refresh ? "?refresh=1" : ""}`,
  );
  return data.folders;
}

// ── Drive gallery ─────────────────────────────────────────────────────────────

export interface GalleryFile {
  fileId: string;
  name: string;
}

/** Booking-level multi-select tags returned by the gallery worker. Used by
 *  the lightbox to compose SEO-friendly download filenames — every photo in
 *  the same Drive folder shares the same tag set. */
export interface GalleryTags {
  kimono_tag: string[];
  shoot_tag: string[];
  season_tag: string[];
  scene_tag: string[];
  kimono_color_tag: string[];
}

export interface GalleryResponse {
  files: GalleryFile[];
  total: number;
  folderId: string;
  tags: GalleryTags;
  /** True when the customer's original link was empty/dead and these images
   *  come from the admin's backup copy instead. */
  usingBackup?: boolean;
}

export async function fetchGallery(recordId: string): Promise<GalleryResponse> {
  return apiFetch<GalleryResponse>(`/photos/${encodeURIComponent(recordId)}/gallery`);
}

// ── Photo field patch ─────────────────────────────────────────────────────────

export interface PatchPhotoPayload {
  liked?: boolean;
  /** Toggle per-photographer like — requires valid JWT */
  likedBy?: "add" | "remove";
  downloaded?: boolean;
  /** Admin-only: update tag fields */
  tags?: Partial<Record<"kimono_tag" | "shoot_tag" | "season_tag" | "scene_tag" | "kimono_color_tag", string[]>>;
  /** Admin-only: save admin backup Drive link after import */
  adminDriveLink?: string;
  /** Admin-only: rename folder display name */
  adminFolderName?: string;
  /** Admin-only: parent/category folder on the admin's Drive (e.g. "Couples").
   *  Set at import + one-time backfill; powers the "filter by Drive folder" view. */
  adminCategoryFolder?: string;
  /** Admin-only: replace the photographer's link (wrong set sent). The worker
   *  also re-queues the preview sync. */
  driveLink?: string;
  /** With driveLink: blank the admin-Drive import (copied from the old link). */
  clearAdminImport?: boolean;
}

export async function patchPhoto(
  id: string,
  patch: PatchPhotoPayload
): Promise<void> {
  // Send JWT so worker knows which photographer is toggling likedBy
  await apiFetch<{ ok: boolean }>(
    `/photos/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
    /* withAuth */ true
  );
}

// ── Requeue Preview Sync (admin) ─────────────────────────────────────────────

/** Admin-only: set Preview Sync = Queued so the cron re-runs the preview pipeline. */
export async function requeuePreviewSync(recordId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/photos/${encodeURIComponent(recordId)}/requeue-preview`,
    { method: "POST" },
    /* withAuth */ true,
  );
}

// ── Send to Caption ──────────────────────────────────────────────────────────

export interface SendToCaptionResponse {
  ok: boolean;
  recordId: string;
  captionUrl: string;
  photoCount: number;
}

/** Send selected photos to caption system as a Draft record. */
export async function sendToCaption(
  recordId: string,
  fileIds: string[],
): Promise<SendToCaptionResponse> {
  return apiFetch<SendToCaptionResponse>(
    `/photos/${encodeURIComponent(recordId)}/send-to-caption`,
    { method: "POST", body: JSON.stringify({ fileIds }) },
    /* withAuth */ true,
  );
}

// ── Send to Asakusa Map ─────────────────────────────────────────────────────

export type MapAreaId = "sensoji" | "sumida-park" | "west-asakusa";

export interface SendToMapResponse {
  ok: boolean;
  area: MapAreaId;
  pinNumber: number;
  uploadedCount: number;
  totalRequested: number;
  urls: string[];
  errors?: Record<string, string>;
}

/** Send selected photos to an Asakusa Map pin via Cloudinary upload. */
export async function sendToMap(
  recordId: string,
  fileIds: string[],
  pinNumber: number,
  area: MapAreaId,
): Promise<SendToMapResponse> {
  return apiFetch<SendToMapResponse>(
    `/photos/${encodeURIComponent(recordId)}/send-to-map`,
    { method: "POST", body: JSON.stringify({ fileIds, pinNumber, area }) },
    /* withAuth */ true,
  );
}

/** Fetch pin data from the Asakusa Map for a specific area. */
export async function fetchMapPins(
  area: MapAreaId = "sensoji",
): Promise<Array<{ number: number; name: string; nameJa: string }>> {
  try {
    const res = await fetch(
      `https://kra-asakusa-map-v2.pages.dev/api/pins?area=${area}`,
    );
    if (!res.ok) return [];
    const pins = (await res.json()) as Array<{
      number: number;
      name: string;
      nameJa: string;
    }>;
    return pins
      .filter((p) => p.number && p.name)
      .sort((a, b) => a.number - b.number);
  } catch {
    return [];
  }
}

// ── Bulk Import ──────────────────────────────────────────────────────────────

export interface BulkScanCategory {
  name: string;
  folderId: string;
  suggestedTag: string | null;
  folders: Array<{
    id: string;
    name: string;
    link: string;
    suggestedShootTags: string[];
    suggestedSeasonTags: string[];
    suggestedSceneTags: string[];
  }>;
}

export interface BulkScanResponse {
  categories: BulkScanCategory[];
  totalFolders: number;
}

export async function bulkScan(parentFolderId: string): Promise<BulkScanResponse> {
  return apiFetch<BulkScanResponse>(
    "/bulk-scan",
    { method: "POST", body: JSON.stringify({ parentFolderId }) },
    /* withAuth */ true
  );
}

export interface ImportFolder {
  folderId: string;
  folderName: string;
  kimonoTags?: string[];
  shootTags?: string[];
  seasonTags?: string[];
  sceneTags?: string[];
  colorTags?: string[];
  /** Parent/category folder on the admin's Drive the set is copied into
   *  (e.g. "Couples"). Stored on the record to power the Drive-folder filter. */
  categoryFolder?: string;
  /** User-facing category name shown in the Phân loại notification.
   *  Only used when `bulkImport` is called with `notifyAs: "sort"`. */
  sortCategory?: string;
}

export interface BulkImportOptions {
  /** "sort" → server fires a `PHOTO_FOLDER_SORTED` Discord notification
   *  per successfully created record. Omitted for batch imports to avoid
   *  flooding the channel. */
  notifyAs?: "sort";
  /** When true, also copy each registered folder into the admin's own Drive
   *  (third-party photographer folders). Requires `destFolderId` + `oauthToken`.
   *  The copy runs in the background; the record re-points to the copy. */
  copyToMyDrive?: boolean;
  /** Destination Drive folder to copy into (when `copyToMyDrive`). */
  destFolderId?: string;
  /** Google OAuth access token with write access to `destFolderId`. */
  oauthToken?: string;
}

export interface BulkImportResponse {
  created: number;
  errors: Array<{ folderName: string; error: string }>;
}

export async function bulkImport(
  folders: ImportFolder[],
  options?: BulkImportOptions,
): Promise<BulkImportResponse> {
  return apiFetch<BulkImportResponse>(
    "/bulk-import",
    {
      method: "POST",
      body: JSON.stringify({
        folders,
        ...(options?.notifyAs ? { notifyAs: options.notifyAs } : {}),
        ...(options?.copyToMyDrive && options.destFolderId && options.oauthToken
          ? {
              copyToMyDrive: true,
              destFolderId: options.destFolderId,
              oauthToken: options.oauthToken,
            }
          : {}),
      }),
    },
    /* withAuth */ true
  );
}

// ── Resize ──────────────────────────────────────────────────────────────────

export interface ResizeStartResponse {
  ok: boolean;
  jobId: string;
  totalFiles: number;
  totalSize: number;
  totalSizeMB: number;
}

export interface ResizeStatusResponse {
  ok: boolean;
  status: "queued" | "resizing" | "done" | "failed" | "cancelled";
  total: number;
  processed: number;
  failed: number;
  errors: Record<string, string>;
  originalTotalSize: number;
  newTotalSize: number;
  savedMB: number;
  startedAt: string;
  updatedAt: string;
}

export interface ResizeCandidatesResponse {
  ok: boolean;
  folderId: string;
  totalFiles: number;
  totalSize: number;
  totalSizeMB: number;
  exceedsThreshold: boolean;
}

export async function startResize(
  recordId: string,
  driveLink: string,
  chatName: string
): Promise<ResizeStartResponse> {
  return apiFetch<ResizeStartResponse>(
    "/resize/start",
    { method: "POST", body: JSON.stringify({ recordId, driveLink, chatName }) },
    /* withAuth */ true
  );
}

export async function getResizeStatus(jobId: string): Promise<ResizeStatusResponse> {
  return apiFetch<ResizeStatusResponse>(
    `/resize/status?jobId=${encodeURIComponent(jobId)}`,
    undefined,
    /* withAuth */ true
  );
}

export async function cancelResize(jobId: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    "/resize/cancel",
    { method: "POST", body: JSON.stringify({ jobId }) },
    /* withAuth */ true
  );
}

export async function getResizeCandidates(
  driveLink: string
): Promise<ResizeCandidatesResponse> {
  return apiFetch<ResizeCandidatesResponse>(
    `/resize/candidates?driveLink=${encodeURIComponent(driveLink)}`,
    undefined,
    /* withAuth */ true
  );
}

// ── Import Drive ─────────────────────────────────────────────────────────────

export interface StartImportDriveParams {
  oauthToken: string;
  sourceFolderId: string;
  destFolderId: string;
  photoId: string;
  chatName: string;
  /** Custom folder name for the destination (defaults to chatName on server) */
  folderName?: string;
}

/** Kick off a background Drive import job on the worker. Returns immediately. */
export async function startImportDrive(params: StartImportDriveParams): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    "/import-drive",
    { method: "POST", body: JSON.stringify(params) },
    /* withAuth */ true
  );
}

// ── Collections ─────────────────────────────────────────────────────────────

export interface CollectionMeta {
  id: string;
  name: string;
  description: string;
  count: number;
  createdAt: string;
  updatedAt: string;
}

export interface Collection extends CollectionMeta {
  photoIds: string[];
}

export async function fetchCollections(): Promise<CollectionMeta[]> {
  const data = await apiFetch<{ collections: CollectionMeta[] }>(
    "/collections",
    undefined,
    /* withAuth */ true
  );
  return data.collections;
}

export async function fetchCollection(id: string): Promise<Collection> {
  const data = await apiFetch<{ collection: Collection }>(
    `/collections/${encodeURIComponent(id)}`,
    undefined,
    /* withAuth */ true
  );
  return data.collection;
}

export async function createCollection(
  name: string,
  description = ""
): Promise<Collection> {
  const data = await apiFetch<{ collection: Collection }>(
    "/collections",
    { method: "POST", body: JSON.stringify({ name, description }) },
    /* withAuth */ true
  );
  return data.collection;
}

export async function updateCollection(
  id: string,
  patch: { name?: string; description?: string }
): Promise<Collection> {
  const data = await apiFetch<{ collection: Collection }>(
    `/collections/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
    /* withAuth */ true
  );
  return data.collection;
}

export async function deleteCollection(id: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/collections/${encodeURIComponent(id)}`,
    { method: "DELETE" },
    /* withAuth */ true
  );
}

export async function addToCollection(
  collectionId: string,
  photoIds: string[]
): Promise<number> {
  const data = await apiFetch<{ count: number }>(
    `/collections/${encodeURIComponent(collectionId)}/photos`,
    { method: "POST", body: JSON.stringify({ photoIds }) },
    /* withAuth */ true
  );
  return data.count;
}

export async function removeFromCollection(
  collectionId: string,
  photoIds: string[]
): Promise<number> {
  const data = await apiFetch<{ count: number }>(
    `/collections/${encodeURIComponent(collectionId)}/photos`,
    { method: "DELETE", body: JSON.stringify({ photoIds }) },
    /* withAuth */ true
  );
  return data.count;
}

// ── Auto Tag ───────────────────────────────────────────────────────────────

export interface AutoTagResult {
  recordId: string;
  folderName: string;
  tagsAdded: Record<string, string[]>;
  /** Tags that came from AI rather than the name rules. */
  aiTags?: string[];
}

export interface AutoTagResponse {
  updated: number;
  skipped: number;
  dryRun: boolean;
  results: AutoTagResult[];
}

export async function runAutoTag(options: {
  tagGroups?: string[];
  dryRun?: boolean;
  /** AI reads the kimono type from names the rules can't (cheap, in preview too). */
  useAI?: boolean;
  /** AI looks at each cover photo for colour / night (slow, apply only). */
  useVision?: boolean;
}): Promise<AutoTagResponse> {
  return apiFetch<AutoTagResponse>(
    "/photos/auto-tag",
    { method: "POST", body: JSON.stringify(options) },
    /* withAuth */ true
  );
}

// ── Share Links ─────────────────────────────────────────────────────────────

export interface ShareCreateResponse {
  token: string;
  title: string;
  allowDownload: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface ShareMeta {
  token: string;
  title: string;
  allowDownload: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface SharePublicResponse {
  title: string;
  expiresAt: string;
  allowDownload: boolean;
  total: number;
  files: GalleryFile[];
}

/** Build the customer-facing share URL for a given token. */
export function buildShareUrl(token: string): string {
  return `${window.location.origin}/?share=${token}`;
}

export async function createShareLink(params: {
  recordId: string;
  ttlSeconds: number;
  title?: string;
  allowDownload?: boolean;
}): Promise<ShareCreateResponse> {
  return apiFetch<ShareCreateResponse>(
    "/share",
    { method: "POST", body: JSON.stringify(params) },
    /* withAuth */ true,
  );
}

export async function listShareLinks(recordId?: string): Promise<ShareMeta[]> {
  const qs = recordId ? `?recordId=${encodeURIComponent(recordId)}` : "";
  const data = await apiFetch<{ items: ShareMeta[] }>(`/share${qs}`, undefined, /* withAuth */ true);
  return data.items;
}

export async function revokeShareLink(token: string): Promise<void> {
  await apiFetch<{ ok: boolean }>(
    `/share/${encodeURIComponent(token)}`,
    { method: "DELETE" },
    /* withAuth */ true,
  );
}

/** Public share fetch — no auth header sent. */
export async function fetchPublicShare(token: string): Promise<SharePublicResponse> {
  return apiFetch<SharePublicResponse>(`/share/${encodeURIComponent(token)}`);
}
